import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { ListChecks } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth, CHOICE_PROMPT_KEY } from "@/context/AuthContext";
import api from "@/lib/api";

// Shown once per sign-in when a member has open Choices they haven't answered.
export default function ChoicePrompt({ enabled, ready }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const eligible = ready && enabled && user?.role === "team_member" && !!user.terms_accepted_at;

  useEffect(() => {
    if (!eligible || sessionStorage.getItem(CHOICE_PROMPT_KEY) !== "1") return;
    sessionStorage.removeItem(CHOICE_PROMPT_KEY);
    api.get("/member/choices")
      .then(({ data }) => setCount((data.items || []).filter((c) => !c.responded && c.accepting).length))
      .catch(() => {});
  }, [eligible]);

  if (!count) return null;
  const close = () => setCount(0);
  const respond = () => { close(); navigate("/member/choices"); };

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 p-4" data-testid="choice-prompt">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-xl">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-sky-100 text-sky-600"><ListChecks className="h-6 w-6" /></span>
        <h2 className="mt-4 text-lg font-bold text-slate-900 font-heading" data-testid="choice-prompt-title">
          You have {count} {count === 1 ? "Choice" : "Choices"} to answer
        </h2>
        <p className="mt-1 text-sm text-slate-500">Your coach is waiting for your response.</p>
        <div className="mt-5 flex flex-col gap-2 sm:flex-row-reverse">
          <Button onClick={respond} className="flex-1 rounded-full bg-slate-900 hover:bg-slate-800" data-testid="choice-prompt-respond-btn">Respond now</Button>
          <Button variant="ghost" onClick={close} className="flex-1 rounded-full text-slate-500" data-testid="choice-prompt-later-btn">Later</Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
